/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'lib/utils';
import { useFootball3dWeeksAndEventsQuery } from 'features/events/queries';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useServerNow } from 'shared/queries/serverTime';
import { liveUntilSecFromEventLike } from 'features/realtime/liveFromResults';
import { useMobileTabsContext } from 'app/stores/mobile-tabs-store';
import { useOngoingEventQuery } from 'features/video/queries';
import type { Football3dWeek } from '../api';

function WeekSelector() {
  const { t } = useTranslation();
  const { currentScheduleId: current_schedule_id, selectedWeekId, setSelectedWeekId } = useScheduleContext();
  const { switchToBets } = useMobileTabsContext();

  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const scrollDirRef = React.useRef(0);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setHasOverflow(el.scrollWidth > el.clientWidth);
  }, []);

  const stopHoverScroll = React.useCallback(() => {
    scrollDirRef.current = 0;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startHoverScroll = React.useCallback((dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    scrollDirRef.current = dir;
    if (rafRef.current) return;

    const tick = () => {
      const node = scrollerRef.current;
      const d = scrollDirRef.current;
      if (!node || !d) {
        rafRef.current = null;
        return;
      }
      node.scrollLeft += d * 9;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  React.useEffect(() => stopHoverScroll, [stopHoverScroll]);

  const { data, isLoading } = useFootball3dWeeksAndEventsQuery({ scheduleId: current_schedule_id });
  const weeks: Football3dWeek[] = data?.weeks ?? [];
  const serverNowMs = useServerNow();

  const ongoingQuery = useOngoingEventQuery({
    scheduleId: current_schedule_id ?? undefined,
    enabled: current_schedule_id != null,
  });

  const weekEndedByWeekId = React.useMemo(() => {
    const map = new Map<number, boolean>();
    const serverNowSec = serverNowMs / 1000;
    for (const week of weeks) {
      const events = Array.isArray(week.events) ? week.events : [];
      if (events.length === 0) continue;
      const featured = events.find((ev: Record<string, unknown>) => ev.featured === true);
      const anchor = featured ?? events.find((ev: Record<string, unknown>) => liveUntilSecFromEventLike(ev) != null);
      if (!anchor) continue;
      const anchorEnd = liveUntilSecFromEventLike(anchor);
      if (anchorEnd != null && Number.isFinite(anchorEnd)) {
        if (serverNowSec >= anchorEnd) map.set(week.week_id, true);
      } else if (featured && (featured as Record<string, unknown>).status === 'cancelled') {
        map.set(week.week_id, true);
      }
    }
    return map;
  }, [weeks, serverNowMs]);

  const weekKickoffByWeekId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const week of weeks) {
      const events = Array.isArray(week.events) ? week.events : [];
      const ts = events
        .map((e: { event_timestamp?: number }) => Number(e?.event_timestamp))
        .filter((n: number) => Number.isFinite(n) && n > 0)
        .sort((a: number, b: number) => a - b)[0];
      if (ts) map.set(week.week_id, ts);
    }
    return map;
  }, [weeks]);

  const sortedWeeks = React.useMemo(() => {
    const liveWeekId =
      ongoingQuery.data?.week_id != null && weeks.some((w) => w.week_id === Number(ongoingQuery.data.week_id))
        ? Number(ongoingQuery.data.week_id)
        : null;

    const sortByKickoff = (list: Football3dWeek[]) =>
      list.sort((a, b) => {
        const ta = weekKickoffByWeekId.get(a.week_id);
        const tb = weekKickoffByWeekId.get(b.week_id);
        const aHas = ta != null && Number.isFinite(ta) ? 1 : 0;
        const bHas = tb != null && Number.isFinite(tb) ? 1 : 0;
        if (aHas && bHas) {
          if (Number(ta) !== Number(tb)) return Number(ta) - Number(tb);
          return a.week_id - b.week_id;
        }
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        return a.week_id - b.week_id;
      });

    const ended = weeks.filter((w) => w.week_id !== liveWeekId && weekEndedByWeekId.get(w.week_id) === true);
    const future = weeks.filter(
      (w) => w.week_id !== liveWeekId && !weekEndedByWeekId.get(w.week_id)
    );
    sortByKickoff(ended);
    sortByKickoff(future);

    const live = liveWeekId != null ? weeks.filter((w) => w.week_id === liveWeekId) : [];
    return [...ended, ...live, ...future];
  }, [weeks, weekKickoffByWeekId, weekEndedByWeekId, ongoingQuery.data?.week_id]);

  React.useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkOverflow, sortedWeeks.length]);

  const formatEventTime = (timestamp: number | undefined | null): string => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp) * 1000);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const weekLabel = t('WEEK_LABEL') || 'Week';

  React.useEffect(() => {
    if (!sortedWeeks.length) return;
    const liveWeekId =
      ongoingQuery.data?.week_id != null && sortedWeeks.some((w) => w.week_id === Number(ongoingQuery.data.week_id))
        ? Number(ongoingQuery.data.week_id)
        : null;
    const needSync = selectedWeekId == null;
    if (needSync) {
      setSelectedWeekId(liveWeekId ?? sortedWeeks[0].week_id);
    }
  }, [current_schedule_id, sortedWeeks, selectedWeekId, setSelectedWeekId, ongoingQuery.data?.week_id]);

  const shouldScrollRef = React.useRef(false);

  React.useEffect(() => {
    shouldScrollRef.current = true;
  }, [current_schedule_id]);

  React.useEffect(() => {
    if (!shouldScrollRef.current) return;
    if (selectedWeekId == null) return;
    if (sortedWeeks.length === 0) return;
    requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const btn = el.querySelector('[data-selected="true"]');
      if (btn) {
        shouldScrollRef.current = false;
        btn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    });
  }, [selectedWeekId, sortedWeeks.length]);

  const placeholderBar = (
    <div
      className="event-navigator relative w-full z-40 bg-card/95 supports-[backdrop-filter]:bg-card/80 backdrop-blur border-b border-border flex items-center justify-center"
      style={{ height: '56px' }}
    >
      <span className="text-sm text-muted-foreground">
        {isLoading ? t('LOADING') || 'Loading…' : t('NO_EVENTS') || 'No events'}
      </span>
    </div>
  );

  if (isLoading) return placeholderBar;
  if (sortedWeeks.length === 0) return current_schedule_id != null ? placeholderBar : null;

  return (
    <div
      className="event-navigator relative w-full z-40 bg-card/95 supports-[backdrop-filter]:bg-card/80 backdrop-blur border-b border-border"
      style={{ height: '56px' }}
    >
      {hasOverflow && (
        <div className="hidden [@media(hover:hover)]:flex absolute inset-y-0 left-1 items-center z-50">
          <button
            type="button"
            className="px-2 py-1 rounded-md text-xs font-bold bg-card/80 supports-[backdrop-filter]:bg-card/60 backdrop-blur border border-border shadow-sm hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-offset-2"
            aria-label="Scroll weeks left"
            title="Scroll left"
            onPointerEnter={() => startHoverScroll(-1)}
            onPointerLeave={stopHoverScroll}
            onPointerDown={stopHoverScroll}
            onClick={() => scrollerRef.current?.scrollBy?.({ left: -240, behavior: 'smooth' })}
          >
            {'<<'}
          </button>
        </div>
      )}
      {hasOverflow && (
        <div className="hidden [@media(hover:hover)]:flex absolute inset-y-0 right-1 items-center z-50">
          <button
            type="button"
            className="px-2 py-1 rounded-md text-xs font-bold bg-card/80 supports-[backdrop-filter]:bg-card/60 backdrop-blur border border-border shadow-sm hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-offset-2"
            aria-label="Scroll weeks right"
            title="Scroll right"
            onPointerEnter={() => startHoverScroll(1)}
            onPointerLeave={stopHoverScroll}
            onPointerDown={stopHoverScroll}
            onClick={() => scrollerRef.current?.scrollBy?.({ left: 240, behavior: 'smooth' })}
          >
            {'>>'}
          </button>
        </div>
      )}

      <div
        ref={scrollerRef}
        className={cn(
          'overflow-y-hidden scroll-smooth h-full',
          hasOverflow ? 'overflow-x-auto' : 'overflow-x-hidden'
        )}
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          direction: 'ltr',
          scrollPaddingLeft: '0px',
          scrollPaddingRight: '0px',
          overflowY: 'hidden',
        }}
      >
        <style>{`
          .event-navigator div::-webkit-scrollbar { display: none; }
          .event-navigator button:hover:not([data-selected="true"]) {
            opacity: 1 !important;
          }
        `}</style>
        <div
          className="flex items-center h-full"
          style={{ paddingLeft: '0px', paddingRight: '0px', minWidth: 'max-content' }}
        >
          {sortedWeeks.map((week, index) => {
            const isSelected = selectedWeekId === week.week_id;
            const isLast = index === sortedWeeks.length - 1;
            const ts = weekKickoffByWeekId.get(week.week_id);
            const labelTime = ts ? formatEventTime(ts) : '';
            const weekEnded = weekEndedByWeekId.get(week.week_id);
            const liveWeekId =
              ongoingQuery.data?.week_id != null
                ? Number(ongoingQuery.data.week_id)
                : null;
            const ongoingWeek = liveWeekId != null && week.week_id === liveWeekId;
            const ongoingCancelled =
              ongoingWeek && ongoingQuery.data?.cancelled === true;
            const serverNowSec = serverNowMs / 1000;
            const kickoffSec = ts != null && Number.isFinite(ts) ? Number(ts) : null;
            const futureKickoff = kickoffSec != null && kickoffSec > serverNowSec;
            // Minutes rounded up: 1:01 → "2m", 2:36 → "3m"; when < 60s show seconds countdown.
            const countdownLabel =
              !ongoingWeek && !weekEnded && futureKickoff && kickoffSec != null
                ? (() => {
                    const secLeft = Math.max(0, Math.floor(kickoffSec - serverNowSec));
                    const m = secLeft >= 60 ? Math.ceil(secLeft / 60) : 0;
                    const s = secLeft % 60;
                    return secLeft >= 60 ? `Starts in ${m}m` : `Starts in ${s}s`;
                  })()
                : null;
            const statusText = ongoingCancelled
              ? (t('EVENT_CANCELLED') || 'Cancelled')
              : ongoingWeek
                ? (t('LIVE') || 'LIVE')
                : weekEnded
                  ? (t('EVENT_FINISHED') || 'Finished')
                  : countdownLabel ?? '—';
            return (
              <button
                key={week.week_id}
                data-selected={isSelected}
                data-closed={weekEnded}
                className={cn(
                  'flex flex-col items-center justify-center',
                  'px-5 md:px-7 py-2',
                  'min-w-[90px] md:min-w-[110px]',
                  'h-full',
                  'rounded-none',
                  'transition-colors duration-200 transition-opacity duration-200',
                  'flex-shrink-0',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-inset',
                  !isLast ? 'border-r border-border' : '',
                  'text-[var(--sport-on-color)]'
                )}
                style={{
                  scrollSnapAlign: 'start',
                  backgroundColor: 'var(--sport-color)',
                  opacity: isSelected ? 1 : 0.7,
                  ...(!isSelected && ongoingWeek && !ongoingCancelled ? { borderBottom: '3px solid rgba(255,255,255,0.5)' } : {}),
                  ...(weekEnded ? { borderBottom: '3px solid transparent' } : {}),
                }}
                onClick={() => {
                  setSelectedWeekId(week.week_id);
                  switchToBets();
                }}
                title={`${weekLabel} ${week.week_number}${labelTime ? ` • ${labelTime}` : ''} • ${statusText}`}
              >
                <div className="font-semibold text-sm md:text-base truncate max-w-full whitespace-nowrap">
                  {weekLabel} {week.week_number}{labelTime ? ` · ${labelTime}` : ''}
                </div>
                <div className="text-[10px] min-h-[14px] mt-0.5 font-medium opacity-90 text-[var(--sport-on-color)]">
                  {statusText}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default WeekSelector;
